'use client';

import { Accordion, Button, Icon } from '@/components/ui';
import { cn } from '@/libs';
import type { LuckyTicketsByRaffle } from '@/types/models/LuckyTicketsByRaffle';

import { LuckySequenceSlider, SkeletonLuckySequence } from './components';

import { useLuckySequences } from '../../hooks';

type LuckySequencesAccordionProps = {
  raffleId: string;
  luckySequencyType: string;
  initialFoundSequences?: LuckyTicketsByRaffle;
  initialNotFoundSequences?: LuckyTicketsByRaffle;
  className?: string;
};

export const LuckySequencesAccordion = ({
  raffleId,
  luckySequencyType,
  initialFoundSequences,
  initialNotFoundSequences,
  className,
}: LuckySequencesAccordionProps) => {
  const { loading, error, noLuckySequences, luckySequenceSliderProps } =
    useLuckySequences({
      raffleId,
      foundSequences: initialFoundSequences,
      notFoundSequences: initialNotFoundSequences,
    });

  if (noLuckySequences && !loading) {
    return null;
  }

  const renderContent = () => {
    if (loading) {
      return <SkeletonLuckySequence />;
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center p-8 space-y-2">
          <div className="text-red-600 text-sm">{error}</div>
          <Button
            variant="link"
            className="text-sm text-primary underline"
            onClick={() => window.location.reload()}
          >
            Tentar novamente
          </Button>
        </div>
      );
    }

    if (noLuckySequences) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="text-foreground/60">
            Não há raspadinhas premiadas registradas.
          </div>
        </div>
      );
    }

    return (
      <div>
        <LuckySequenceSlider {...luckySequenceSliderProps} />
      </div>
    );
  };

  return (
    <Accordion
      showLine={false}
      defaultOpen={true}
      className={cn('w-full p-4 bg-background-dark rounded-xl', className)}
      headerContent={
        <div className="flex items-center space-x-2">
          <Icon.Lightning width={16} height={16} className="fill-brand" />
          {luckySequencyType === 'SCRATCHER' ? (
            <h3 className="text-left font-bold">Raspadinhas Premiadas</h3>
          ) : (
            <h3 className="text-left font-bold">Premiações Instantâneas</h3>
          )}
        </div>
      }
    >
      {renderContent()}
    </Accordion>
  );
};
